import { createI18n } from 'vue-i18n'

const supportedLanguages = ['zh-CN', 'en-US', 'ja-JP', 'ko-KR', 'fr-FR', 'de-DE', 'es-ES']

const detectUserLanguage = () => {
  const savedLanguage = localStorage.getItem('language')
  if (savedLanguage && supportedLanguages.includes(savedLanguage)) {
    return savedLanguage
  }

  const browserLang = navigator.language || navigator.userLanguage
  const browserLangCode = browserLang.split('-')[0]

  const languageMap = {
    'zh': 'zh-CN',
    'en': 'en-US',
    'ja': 'ja-JP',
    'ko': 'ko-KR',
    'fr': 'fr-FR',
    'de': 'de-DE',
    'es': 'es-ES'
  }

  const detectedLanguage = languageMap[browserLangCode] || 'zh-CN'
  localStorage.setItem('language', detectedLanguage)
  return detectedLanguage
}

const messages = {
  'zh-CN': {
    common: {
      title: 'FileStation',
      subtitle: '文件中转站',
      description: '安全、快速的文件传输解决方案',
      tagline: '设置文件有效时长，获取取件码，轻松分享文件',
      mainTitle: '文件传输中心',
      mainSubtitle: '安全、快速的文件中转站'
    },
    upload: {
      title: '上传文件',
      dragHint: '拖拽',
      clickHint: '点击',
      pasteHint: '或 Ctrl + V 粘贴上传',
      expiration: '文件有效时长：',
      theme: '主题：',
      expireOptions: {
        oneDay: '1天',
        threeDays: '3天',
        sevenDays: '7天',
        fifteenDays: '15天',
        thirtyDays: '30天'
      },
      themeOptions: {
        auto: '跟随系统',
        light: '亮色',
        dark: '暗色'
      },
      pickupCode: '取件码',
      expireTime: '过期时间',
      fileName: '文件名',
      fileSize: '文件大小',
      fileExpiry: '过期时间',
      shareTemplate: {
        sharedVia: '通过 FileStation 分享的文件',
        shareButton: '分享',
        link: '链接',
        extractionCode: '提取码',
        validUntil: '有效期',
        copySuccess: '分享内容已复制到剪贴板！',
        copyFailed: '复制失败，请手动复制'
      }
    },
    retrieve: {
      title: '文件取件',
      inputPlaceholder: '请输入取件码',
      button: '取件',
      error: {
        empty: '请输入取件码',
        invalid: '取件码应为6位',
        notFound: '文件不存在或已过期',
        failed: '下载失败，请稍后重试'
      }
    },
    settings: {
      urlForm: '链接格式设置',
      upload: '上传设置',
      defaultCopy: '默认复制链接',
      customUrl: '自定义链接',
      enableCustom: '启用自定义',
      customPrefix: '自定义前缀',
      uploadChannel: '上传渠道',
      channelType: '渠道类型',
      channelName: '渠道名称',
      autoSelect: '自动选择',
      uploadFolder: '上传目录',
      autoSwitch: '自动切换',
      uploadNaming: '文件命名方式',
      filePreprocessing: '文件预处理',
      convertToWebp: '转换为WebP',
      compress: '文件压缩',
      compressThreshold: '压缩阈值',
      compressTarget: '期望大小',
      serverCompress: '服务端压缩',
      enableCompress: '开启压缩',
      confirm: '确定',
      urlTypes: {
        url: '原始链接',
        md: 'MarkDown',
        html: 'HTML',
        ubb: 'BBCode'
      },
      namingTypes: {
        default: '默认',
        index: '仅前缀',
        origin: '仅原名',
        short: '短链接'
      },
      channelTypes: {
        telegram: 'TG',
        cfr2: 'R2',
        s3: 'S3',
        discord: 'DC',
        huggingface: 'HF'
      },
      placeholders: {
        customUrlPrefix: '请输入自定义链接前缀',
        uploadFolder: '请输入上传目录路径'
      }
    },
    theme: {
      light: '浅色模式',
      dark: '深色模式',
      auto: '自动模式'
    },
    footer: {
      copyright: '© 2026 FileStation. 安全、高效的文件中转站'
    },
    userGuide: {
      title: '使用说明',
      coreFeatures: {
        title: '一、核心功能',
        description: 'FileStation - 安全高效的文件中转站',
        features: {
          upload: '文件上传：支持拖拽、点击、粘贴上传',
          security: '安全传输：加密技术保障文件安全',
          share: '快速分享：生成取件码，一键分享',
          expiration: '时效管理：支持设置1-30天有效期',
          multiLanguage: '多语言支持：支持7种语言切换'
        }
      },
      operationGuide: {
        title: '二、操作指南',
        upload: {
          title: '上传文件',
          steps: [
            '选择上传方式：拖拽文件到上传区域、点击上传区域选择文件、复制文件后按 Ctrl + V 粘贴',
            '设置有效期：选择有效期1天、3天、7天、15天、30天',
            '获取取件码：上传完成后自动生成取件码、点击"分享"按钮生成完整分享文案'
          ]
        },
        retrieve: {
          title: '取件下载',
          steps: [
            '输入6位取件码',
            '点击"取件"按钮',
            '下载文件'
          ]
        }
      },
      faq: {
        title: '三、常见问题',
        q1: {
          question: 'Q1：支持哪些文件格式？',
          answer: 'A：支持所有常见文件格式，包括图片、视频、音频、文档、压缩文件等。'
        },
        q2: {
          question: 'Q2：文件大小有限制吗？',
          answer: 'A：单个文件最大支持2GB。'
        },
        q3: {
          question: 'Q3：取件码有效期是多久？',
          answer: 'A：由上传时设置，支持1-30天。过期后文件自动删除。'
        },
        q4: {
          question: 'Q4：文件上传失败怎么办？',
          answer: 'A：检查网络连接，刷新页面后重新上传。'
        },
        q5: {
          question: 'Q5：可以同时上传多个文件吗？',
          answer: 'A：支持批量上传，每个文件独立生成取件码。'
        },
        q6: {
          question: 'Q6：如何分享文件给他人？',
          answer: 'A：上传完成后，可复制取件码、链接或使用"分享"功能生成完整文案。'
        }
      },
      notices: {
        title: '四、注意事项',
        important: {
          title: '重要提醒',
          items: [
            '文件有效期：请及时下载文件，过期后将无法取件，建议在上传后尽快分享给接收方',
            '取件码安全：请妥善保管取件码，不要泄露给无关人员',
            '文件内容：请勿上传违法违规内容，请勿上传涉及隐私和机密的文件',
            '网络环境：建议在稳定的网络环境下使用，大文件上传时请勿关闭页面',
            '浏览器兼容：推荐使用 Chrome、Firefox、Edge、Safari 等现代浏览器'
          ]
        },
        suggestions: {
          title: '使用建议',
          items: [
            '文件命名：使用清晰的文件名，避免特殊字符和空格',
            '有效期设置：临时分享选1-3天，短期分享选7天，长期分享选15-30天',
            '批量上传：多个文件分别上传，每个文件独立生成取件码',
            '分享方式：取件码适合即时通讯，链接适合邮件和论坛'
          ]
        }
      },
      toggle: {
        show: '显示使用说明',
        hide: '隐藏使用说明'
      }
    },
    announcement: {
      title: '公告',
      confirm: '我已知晓！'
    },
    history: {
      title: '上传历史'
    },
    language: {
      zhCN: '中文',
      enUS: 'English',
      jaJP: '日本語',
      koKR: '한국어',
      frFR: 'Français',
      deDE: 'Deutsch',
      esES: 'Español'
    }
  },
  'en-US': {
    common: {
      title: 'FileStation',
      subtitle: 'File Transfer Station',
      description: 'Secure and fast file transfer solution',
      tagline: 'Set file expiration time, get pickup code, easily share files',
      mainTitle: 'File Transfer Center',
      mainSubtitle: 'Secure and fast file transfer station'
    },
    upload: {
      title: 'Upload Files',
      dragHint: 'Drag',
      clickHint: 'Click',
      pasteHint: 'or Ctrl + V to paste',
      expiration: 'File validity period:',
      theme: 'Theme:',
      expireOptions: {
        oneDay: '1 day',
        threeDays: '3 days',
        sevenDays: '7 days',
        fifteenDays: '15 days',
        thirtyDays: '30 days'
      },
      themeOptions: {
        auto: 'Follow system',
        light: 'Light',
        dark: 'Dark'
      },
      pickupCode: 'Pickup Code',
      expireTime: 'Expiration',
      fileName: 'File Name',
      fileSize: 'File Size',
      fileExpiry: 'Expiration',
      shareTemplate: {
        sharedVia: 'File shared via FileStation',
        shareButton: 'Share',
        link: 'Link',
        extractionCode: 'Extraction code',
        validUntil: 'Valid until',
        copySuccess: 'Share content copied to clipboard!',
        copyFailed: 'Copy failed, please copy manually'
      }
    },
    retrieve: {
      title: 'Retrieve Files',
      inputPlaceholder: 'Please enter pickup code',
      button: 'Retrieve',
      error: {
        empty: 'Please enter pickup code',
        invalid: 'Pickup code should be 6 digits',
        notFound: 'File does not exist or has expired',
        failed: 'Download failed, please try again later'
      }
    },
    settings: {
      urlForm: 'Link Format Settings',
      upload: 'Upload Settings',
      defaultCopy: 'Default Copy Link',
      customUrl: 'Custom Link',
      enableCustom: 'Enable Custom',
      customPrefix: 'Custom Prefix',
      uploadChannel: 'Upload Channel',
      channelType: 'Channel Type',
      channelName: 'Channel Name',
      autoSelect: 'Auto Select',
      uploadFolder: 'Upload Folder',
      autoSwitch: 'Auto Switch',
      uploadNaming: 'File Naming Method',
      filePreprocessing: 'File Preprocessing',
      convertToWebp: 'Convert to WebP',
      compress: 'File Compression',
      compressThreshold: 'Compression Threshold',
      compressTarget: 'Target Size',
      serverCompress: 'Server Compression',
      enableCompress: 'Enable Compression',
      confirm: 'Confirm',
      urlTypes: {
        url: 'Original Link',
        md: 'MarkDown',
        html: 'HTML',
        ubb: 'BBCode'
      },
      namingTypes: {
        default: 'Default',
        index: 'Only Prefix',
        origin: 'Only Original Name',
        short: 'Short Link'
      },
      channelTypes: {
        telegram: 'TG',
        cfr2: 'R2',
        s3: 'S3',
        discord: 'DC',
        huggingface: 'HF'
      },
      placeholders: {
        customUrlPrefix: 'Please enter custom link prefix',
        uploadFolder: 'Please enter upload folder path'
      }
    },
    theme: {
      light: 'Light Mode',
      dark: 'Dark Mode',
      auto: 'Auto Mode'
    },
    footer: {
      copyright: '© 2026 FileStation. Secure and efficient file transfer station'
    },
    userGuide: {
      title: 'User Guide',
      coreFeatures: {
        title: '1. Core Features',
        description: 'FileStation - Secure and efficient file transfer station',
        features: {
          upload: 'File Upload: Supports drag, click, and paste upload',
          security: 'Secure Transfer: Encryption technology ensures file security',
          share: 'Quick Share: Generate pickup code, one-click share',
          expiration: 'Expiration Management: Supports 1-30 day validity period',
          multiLanguage: 'Multi-language Support: Supports 7 language switching'
        }
      },
      operationGuide: {
        title: '2. Operation Guide',
        upload: {
          title: 'Upload Files',
          steps: [
            'Choose upload method: Drag files to upload area, click upload area to select files, or press Ctrl + V after copying files',
            'Set expiration: Choose validity period of 1 day, 3 days, 7 days, 15 days, or 30 days',
            'Get pickup code: Pickup code automatically generated after upload, click "Share" button to generate complete share message'
          ]
        },
        retrieve: {
          title: 'Retrieve Files',
          steps: [
            'Enter 6-digit pickup code',
            'Click "Retrieve" button',
            'Download files'
          ]
        }
      },
      faq: {
        title: '3. Frequently Asked Questions',
        q1: {
          question: 'Q1: What file formats are supported?',
          answer: 'A: All common file formats are supported, including images, videos, audio, documents, compressed files, etc.'
        },
        q2: {
          question: 'Q2: Is there a file size limit?',
          answer: 'A: Single file maximum size is 2GB.'
        },
        q3: {
          question: 'Q3: How long is the pickup code valid?',
          answer: 'A: Set during upload, supports 1-30 days. Files are automatically deleted after expiration.'
        },
        q4: {
          question: 'Q4: What if file upload fails?',
          answer: 'A: Check network connection, refresh the page and upload again.'
        },
        q5: {
          question: 'Q5: Can I upload multiple files at once?',
          answer: 'A: Batch upload is supported, each file generates an independent pickup code.'
        },
        q6: {
          question: 'Q6: How to share files with others?',
          answer: 'A: After upload, you can copy the pickup code, link, or use the "Share" function to generate complete message.'
        }
      },
      notices: {
        title: '4. Important Notes',
        important: {
          title: 'Important Reminders',
          items: [
            'File Expiration: Download files in time, they cannot be retrieved after expiration. It is recommended to share with recipients as soon as possible after upload',
            'Pickup Code Security: Keep pickup codes secure and do not disclose them to unrelated personnel',
            'File Content: Do not upload illegal or prohibited content, do not upload files involving privacy and confidentiality',
            'Network Environment: It is recommended to use in a stable network environment, do not close the page during large file uploads',
            'Browser Compatibility: Recommended to use modern browsers such as Chrome, Firefox, Edge, Safari'
          ]
        },
        suggestions: {
          title: 'Usage Suggestions',
          items: [
            'File Naming: Use clear file names, avoid special characters and spaces',
            'Expiration Setting: Temporary sharing select 1-3 days, short-term sharing select 7 days, long-term sharing select 15-30 days',
            'Batch Upload: Upload multiple files separately, each file generates an independent pickup code',
            'Sharing Method: Pickup code is suitable for instant messaging, link is suitable for email and forums'
          ]
        }
      },
      toggle: {
        show: 'Show User Guide',
        hide: 'Hide User Guide'
      }
    },
    announcement: {
      title: 'Announcement',
      confirm: 'I understand!'
    },
    history: {
      title: 'Upload History'
    },
    language: {
      zhCN: '中文',
      enUS: 'English',
      jaJP: '日本語',
      koKR: '한국어',
      frFR: 'Français',
      deDE: 'Deutsch',
      esES: 'Español'
    }
  },
  'ja-JP': {
    common: {
      title: 'FileStation',
      subtitle: 'ファイル転送ステーション',
      description: '安全で高速なファイル転送ソリューション',
      tagline: 'ファイルの有効期限を設定して受取コードを取得し、簡単にファイルを共有',
      mainTitle: 'ファイル転送センター',
      mainSubtitle: '安全で高速なファイル転送ステーション'
    },
    upload: {
      title: 'ファイルアップロード',
      dragHint: 'ドラッグ',
      clickHint: 'クリック',
      pasteHint: 'または Ctrl + V で貼り付け',
      expiration: 'ファイル有効期間：',
      theme: 'テーマ：',
      expireOptions: {
        oneDay: '1日',
        threeDays: '3日',
        sevenDays: '7日',
        fifteenDays: '15日',
        thirtyDays: '30日'
      },
      themeOptions: {
        auto: 'システムに従う',
        light: 'ライト',
        dark: 'ダーク'
      },
      pickupCode: 'ピックアップコード',
      expireTime: '有効期限',
      fileName: 'ファイル名',
      fileSize: 'ファイルサイズ',
      fileExpiry: '有効期限',
      shareTemplate: {
        sharedVia: 'FileStation経由で共有されたファイル',
        shareButton: '共有',
        link: 'リンク',
        extractionCode: '抽出コード',
        validUntil: '有効期限',
        copySuccess: '共有内容をクリップボードにコピーしました！',
        copyFailed: 'コピーに失敗しました。手動でコピーしてください'
      }
    },
    retrieve: {
      title: 'ファイル受取',
      inputPlaceholder: '受取コードを入力してください',
      button: '受取',
      error: {
        empty: '受取コードを入力してください',
        invalid: '受取コードは6桁である必要があります',
        notFound: 'ファイルが存在しないか、期限切れです',
        failed: 'ダウンロードに失敗しました。後でもう一度お試しください'
      }
    },
    settings: {
      urlForm: 'リンク形式設定',
      upload: 'アップロード設定',
      defaultCopy: 'デフォルトのリンクをコピー',
      customUrl: 'カスタムリンク',
      enableCustom: 'カスタムを有効にする',
      customPrefix: 'カスタムプレフィックス',
      uploadChannel: 'アップロードチャネル',
      channelType: 'チャネルタイプ',
      channelName: 'チャネル名',
      autoSelect: '自動選択',
      uploadFolder: 'アップロードフォルダ',
      autoSwitch: '自動切り替え',
      uploadNaming: 'ファイル命名方式',
      filePreprocessing: 'ファイル前処理',
      convertToWebp: 'WebPに変換',
      compress: 'ファイル圧縮',
      compressThreshold: '圧縮しきい値',
      compressTarget: '目標サイズ',
      serverCompress: 'サーバー側圧縮',
      enableCompress: '圧縮を有効にする',
      confirm: '確認',
      urlTypes: {
        url: 'オリジナルリンク',
        md: 'MarkDown',
        html: 'HTML',
        ubb: 'BBCode'
      },
      namingTypes: {
        default: 'デフォルト',
        index: 'プレフィックスのみ',
        origin: '元の名前のみ',
        short: '短縮リンク'
      },
      channelTypes: {
        telegram: 'TG',
        cfr2: 'R2',
        s3: 'S3',
        discord: 'DC',
        huggingface: 'HF'
      },
      placeholders: {
        customUrlPrefix: 'カスタムリンクのプレフィックスを入力してください',
        uploadFolder: 'アップロードフォルダのパスを入力してください'
      }
    },
    theme: {
      light: 'ライトモード',
      dark: 'ダークモード',
      auto: '自動モード'
    },
    footer: {
      copyright: '© 2026 FileStation. 安全で高速なファイル転送ステーション'
    },
    userGuide: {
      title: '使用ガイド',
      coreFeatures: {
        title: '一、コア機能',
        description: 'FileStation - 安全で効率的なファイル転送ステーション',
        features: {
          upload: 'ファイルアップロード：ドラッグ、クリック、貼り付けによるアップロードをサポート',
          security: '安全な転送：暗号化技術によりファイルのセキュリティを確保',
          share: '高速共有：ピックアップコードを生成し、ワンクリックで共有',
          expiration: '有効期限管理：1〜30日の有効期限を設定可能',
          multiLanguage: '多言語サポート：7言語の切り替えをサポート'
        }
      },
      operationGuide: {
        title: '二、操作ガイド',
        upload: {
          title: 'ファイルアップロード',
          steps: [
            'アップロード方法を選択：ファイルをアップロードエリアにドラッグ、アップロードエリアをクリックしてファイルを選択、またはファイルをコピーしてCtrl + Vを押して貼り付け',
            '有効期限を設定：1日、3日、7日、15日、30日の有効期限を選択',
            'ピックアップコードを取得：アップロード完了後にピックアップコードが自動生成、「共有」ボタンをクリックして完全な共有メッセージを生成'
          ]
        },
        retrieve: {
          title: 'ファイル受取',
          steps: [
            '6桁のピックアップコードを入力',
            '「受取」ボタンをクリック',
            'ファイルをダウンロード'
          ]
        }
      },
      faq: {
        title: '三、よくある質問',
        q1: {
          question: 'Q1：どのファイル形式がサポートされていますか？',
          answer: 'A：画像、動画、音声、ドキュメント、圧縮ファイルなど、すべての一般的なファイル形式がサポートされています。'
        },
        q2: {
          question: 'Q2：ファイルサイズに制限はありますか？',
          answer: 'A：単一ファイルの最大サイズは2GBです。'
        },
        q3: {
          question: 'Q3：ピックアップコードの有効期限はどれくらいですか？',
          answer: 'A：アップロード時に設定、1〜30日をサポート。有効期限が切れるとファイルは自動的に削除されます。'
        },
        q4: {
          question: 'Q4：ファイルのアップロードに失敗した場合は？',
          answer: 'A：ネットワーク接続を確認し、ページを更新して再度アップロードしてください。'
        },
        q5: {
          question: 'Q5：複数のファイルを同時にアップロードできますか？',
          answer: 'A：バッチアップロードがサポートされており、各ファイルは独立したピックアップコードを生成します。'
        },
        q6: {
          question: 'Q6：他の人にファイルを共有するには？',
          answer: 'A：アップロード完了後、ピックアップコード、リンクをコピーするか、「共有」機能を使用して完全なメッセージを生成できます。'
        }
      },
      notices: {
        title: '四、注意事項',
        important: {
          title: '重要な注意事項',
          items: [
            'ファイル有効期限：有効期限が切れるとファイルを取得できません。アップロード後はできるだけ早く受取者に共有することをお勧めします',
            'ピックアップコードのセキュリティ：ピックアップコードを安全に保管し、関係者以外に漏洩しないでください',
            'ファイル内容：違法または禁止されたコンテンツをアップロードしないでください。プライバシーや機密に関わるファイルをアップロードしないでください',
            'ネットワーク環境：安定したネットワーク環境での使用をお勧めします。大容量ファイルのアップロード中はページを閉じないでください',
            'ブラウザ互換性：Chrome、Firefox、Edge、Safariなどの最新ブラウザの使用をお勧めします'
          ]
        },
        suggestions: {
          title: '使用に関する提案',
          items: [
            'ファイル命名：明確なファイル名を使用し、特殊文字とスペースを避けてください',
            '有効期限設定：一時的な共有は1〜3日、短期間の共有は7日、長期間の共有は15〜30日を選択',
            'バッチアップロード：複数のファイルを個別にアップロードし、各ファイルは独立したピックアップコードを生成',
            '共有方法：ピックアップコードはインスタントメッセージングに適しており、リンクはメールやフォーラムに適しています'
          ]
        }
      },
      toggle: {
        show: '使用ガイドを表示',
        hide: '使用ガイドを非表示'
      }
    },
    announcement: {
      title: 'お知らせ',
      confirm: '了解しました！'
    },
    history: {
      title: 'アップロード履歴'
    },
    language: {
      zhCN: '中国語',
      enUS: 'English',
      jaJP: '日本語',
      koKR: '한국어',
      frFR: 'Français',
      deDE: 'Deutsch',
      esES: 'Español'
    }
  },
  'ko-KR': {
    common: {
      title: 'FileStation',
      subtitle: '파일 전송 스테이션',
      description: '안전하고 빠른 파일 전송 솔루션',
      tagline: '파일 유효 기간을 설정하고 수취 코드를 받아 파일을 쉽게 공유',
      mainTitle: '파일 전송 센터',
      mainSubtitle: '안전하고 빠른 파일 전송 스테이션'
    },
    upload: {
      title: '파일 업로드',
      dragHint: '드래그',
      clickHint: '클릭',
      pasteHint: '또는 Ctrl + V로 붙여넣기',
      expiration: '파일 유효 기간：',
      theme: '테마：',
      expireOptions: {
        oneDay: '1일',
        threeDays: '3일',
        sevenDays: '7일',
        fifteenDays: '15일',
        thirtyDays: '30일'
      },
      themeOptions: {
        auto: '시스템 설정 따르기',
        light: '밝은 색상',
        dark: '어두운 색상'
      },
      pickupCode: '수취 코드',
      expireTime: '만료 시간',
      fileName: '파일명',
      fileSize: '파일 크기',
      fileExpiry: '만료 시간',
      shareTemplate: {
        sharedVia: 'FileStation을 통해 공유된 파일',
        shareButton: '공유',
        link: '링크',
        extractionCode: '추출 코드',
        validUntil: '유효 기간',
        copySuccess: '공유 내용이 클립보드에 복사되었습니다!',
        copyFailed: '복사 실패, 수동으로 복사하세요'
      }
    },
    retrieve: {
      title: '파일 수취',
      inputPlaceholder: '수취 코드를 입력하세요',
      button: '수취',
      error: {
        empty: '수취 코드를 입력하세요',
        invalid: '수취 코드는 6자리여야 합니다',
        notFound: '파일이 존재하지 않거나 만료되었습니다',
        failed: '다운로드에 실패했습니다. 나중에 다시 시도해 주세요'
      }
    },
    settings: {
      urlForm: '링크 형식 설정',
      upload: '업로드 설정',
      defaultCopy: '기본 링크 복사',
      customUrl: '사용자 정의 링크',
      enableCustom: '사용자 정의 사용',
      customPrefix: '사용자 정의 접두사',
      uploadChannel: '업로드 채널',
      channelType: '채널 유형',
      channelName: '채널 이름',
      autoSelect: '자동 선택',
      uploadFolder: '업로드 폴더',
      autoSwitch: '자동 전환',
      uploadNaming: '파일 명명 방식',
      filePreprocessing: '파일 전처리',
      convertToWebp: 'WebP로 변환',
      compress: '파일 압축',
      compressThreshold: '압축 임계값',
      compressTarget: '목표 크기',
      serverCompress: '서버 측 압축',
      enableCompress: '압축 사용',
      confirm: '확인',
      urlTypes: {
        url: '원본 링크',
        md: 'MarkDown',
        html: 'HTML',
        ubb: 'BBCode'
      },
      namingTypes: {
        default: '기본',
        index: '접두사만',
        origin: '원본 이름만',
        short: '짧은 링크'
      },
      channelTypes: {
        telegram: 'TG',
        cfr2: 'R2',
        s3: 'S3',
        discord: 'DC',
        huggingface: 'HF'
      },
      placeholders: {
        customUrlPrefix: '사용자 정의 링크 접두사를 입력하세요',
        uploadFolder: '업로드 폴더 경로를 입력하세요'
      }
    },
    theme: {
      light: '밝은 모드',
      dark: '어두운 모드',
      auto: '자동 모드'
    },
    footer: {
      copyright: '© 2026 FileStation. 안전하고 효율적인 파일 전송 스테이션'
    },
    userGuide: {
      title: '사용 가이드',
      coreFeatures: {
        title: '1. 핵심 기능',
        description: 'FileStation - 안전하고 효율적인 파일 전송 스테이션',
        features: {
          upload: '파일 업로드: 드래그, 클릭, 붙여넣기 업로드 지원',
          security: '안전한 전송: 암호화 기술로 파일 보안 보장',
          share: '빠른 공유: 수령 코드 생성, 원클릭 공유',
          expiration: '유효기간 관리: 1-30일 유효기간 설정 지원',
          multiLanguage: '다국어 지원: 7개 언어 전환 지원'
        }
      },
      operationGuide: {
        title: '2. 운영 가이드',
        upload: {
          title: '파일 업로드',
          steps: [
            '업로드 방법 선택: 파일을 업로드 영역으로 드래그, 업로드 영역 클릭하여 파일 선택, 또는 파일 복사 후 Ctrl + V로 붙여넣기',
            '유효기간 설정: 1일, 3일, 7일, 15일, 30일 유효기간 선택',
            '수령 코드 가져오기: 업로드 완료 후 자동으로 수령 코드 생성, "공유" 버튼 클릭하여 완전한 공유 메시지 생성'
          ]
        },
        retrieve: {
          title: '파일 수령',
          steps: [
            '6자리 수령 코드 입력',
            '"수령" 버튼 클릭',
            '파일 다운로드'
          ]
        }
      },
      faq: {
        title: '3. 자주 묻는 질문',
        q1: {
          question: 'Q1: 어떤 파일 형식이 지원되나요?',
          answer: 'A: 이미지, 비디오, 오디오, 문서, 압축 파일 등 모든 일반적인 파일 형식이 지원됩니다.'
        },
        q2: {
          question: 'Q2: 파일 크기 제한이 있나요?',
          answer: 'A: 단일 파일 최대 크기는 2GB입니다.'
        },
        q3: {
          question: 'Q3: 수령 코드 유효기간은 얼마인가요?',
          answer: 'A: 업로드 시 설정, 1-30일 지원. 유효기간이 지나면 파일이 자동으로 삭제됩니다.'
        },
        q4: {
          question: 'Q4: 파일 업로드가 실패하면 어떻게 하나요?',
          answer: 'A: 네트워크 연결을 확인하고, 페이지를 새로고침한 후 다시 업로드하세요.'
        },
        q5: {
          question: 'Q5: 여러 파일을 동시에 업로드할 수 있나요?',
          answer: 'A: 일괄 업로드가 지원되며, 각 파일은 독립적인 수령 코드를 생성합니다.'
        },
        q6: {
          question: 'Q6: 다른 사람에게 파일을 공유하려면 어떻게 하나요?',
          answer: 'A: 업로드 완료 후 수령 코드, 링크를 복사하거나 "공유" 기능을 사용하여 완전한 메시지를 생성할 수 있습니다.'
        }
      },
      notices: {
        title: '4. 중요 사항',
        important: {
          title: '중요한 알림',
          items: [
            '파일 유효기간: 제때 파일을 다운로드하세요. 유효기간이 지나면 수령할 수 없습니다. 업로드 후 가능한 한 빨리 수령자에게 공유하는 것이 좋습니다',
            '수령 코드 보안: 수령 코드를 안전하게 보관하고 무관한 사람에게 누설하지 마세요',
            '파일 내용: 불법 또는 금지된 콘텐츠를 업로드하지 마세요. 프라이버시나 기밀과 관련된 파일을 업로드하지 마세요',
            '네트워크 환경: 안정적인 네트워크 환경에서 사용하는 것이 좋습니다. 대용량 파일 업로드 중에는 페이지를 닫지 마세요',
            '브라우저 호환성: Chrome, Firefox, Edge, Safari 등 최신 브라우저 사용을 권장합니다'
          ]
        },
        suggestions: {
          title: '사용 제안',
          items: [
            '파일 명명: 명확한 파일 이름을 사용하고 특수 문자와 공백을 피하세요',
            '유효기간 설정: 임시 공유는 1-3일, 단기 공유는 7일, 장기 공유는 15-30일 선택',
            '일괄 업로드: 여러 파일을 각각 업로드하고 각 파일은 독립적인 수령 코드 생성',
            '공유 방법: 수령 코드는 인스턴트 메시징에 적합하고 링크는 이메일과 포럼에 적합합니다'
          ]
        }
      },
      toggle: {
        show: '사용 가이드 표시',
        hide: '사용 가이드 숨기기'
      }
    },
    announcement: {
      title: '공지사항',
      confirm: '알겠습니다!'
    },
    history: {
      title: '업로드 기록'
    },
    language: {
      zhCN: '중국어',
      enUS: 'English',
      jaJP: '日本語',
      koKR: '한국어',
      frFR: 'Français',
      deDE: 'Deutsch',
      esES: 'Español'
    }
  },
  'fr-FR': {
    common: {
      title: 'FileStation',
      subtitle: 'Station de transfert de fichiers',
      description: 'Solution de transfert de fichiers sécurisée et rapide',
      tagline: 'Définissez la durée de validité du fichier, obtenez le code de récupération et partagez facilement les fichiers',
      mainTitle: 'Centre de transfert de fichiers',
      mainSubtitle: 'Station de transfert de fichiers sécurisée et rapide'
    },
    upload: {
      title: 'Télécharger des fichiers',
      dragHint: 'Glisser',
      clickHint: 'Cliquer',
      pasteHint: 'ou Ctrl + V pour coller',
      expiration: 'Durée de validité du fichier :',
      theme: 'Thème :',
      expireOptions: {
        oneDay: '1 jour',
        threeDays: '3 jours',
        sevenDays: '7 jours',
        fifteenDays: '15 jours',
        thirtyDays: '30 jours'
      },
      themeOptions: {
        auto: 'Suivre le système',
        light: 'Clair',
        dark: 'Sombre'
      },
      pickupCode: 'Code de récupération',
      expireTime: 'Expiration',
      fileName: 'Nom du fichier',
      fileSize: 'Taille du fichier',
      fileExpiry: 'Expiration',
      shareTemplate: {
        sharedVia: 'Fichier partagé via FileStation',
        shareButton: 'Partager',
        link: 'Lien',
        extractionCode: 'Code d\'extraction',
        validUntil: 'Valide jusqu\'au',
        copySuccess: 'Contenu partagé copié dans le presse-papiers !',
        copyFailed: 'Échec de la copie, veuillez copier manuellement'
      }
    },
    retrieve: {
      title: 'Récupérer des fichiers',
      inputPlaceholder: 'Veuillez entrer le code de récupération',
      button: 'Récupérer',
      error: {
        empty: 'Veuillez entrer le code de récupération',
        invalid: 'Le code de récupération doit comporter 6 chiffres',
        notFound: 'Le fichier n\'existe pas ou a expiré',
        failed: 'Échec du téléchargement, veuillez réessayer plus tard'
      }
    },
    settings: {
      urlForm: 'Paramètres du format de lien',
      upload: 'Paramètres de téléchargement',
      defaultCopy: 'Copier le lien par défaut',
      customUrl: 'Lien personnalisé',
      enableCustom: 'Activer la personnalisation',
      customPrefix: 'Préfixe personnalisé',
      uploadChannel: 'Canal de téléchargement',
      channelType: 'Type de canal',
      channelName: 'Nom du canal',
      autoSelect: 'Sélection automatique',
      uploadFolder: 'Dossier de téléchargement',
      autoSwitch: 'Bascule automatique',
      uploadNaming: 'Méthode de nommage des fichiers',
      filePreprocessing: 'Prétraitement des fichiers',
      convertToWebp: 'Convertir en WebP',
      compress: 'Compression de fichiers',
      compressThreshold: 'Seuil de compression',
      compressTarget: 'Taille cible',
      serverCompress: 'Compression côté serveur',
      enableCompress: 'Activer la compression',
      confirm: 'Confirmer',
      urlTypes: {
        url: 'Lien original',
        md: 'MarkDown',
        html: 'HTML',
        ubb: 'BBCode'
      },
      namingTypes: {
        default: 'Par défaut',
        index: 'Préfixe uniquement',
        origin: 'Nom d\'origine uniquement',
        short: 'Lien court'
      },
      channelTypes: {
        telegram: 'TG',
        cfr2: 'R2',
        s3: 'S3',
        discord: 'DC',
        huggingface: 'HF'
      },
      placeholders: {
        customUrlPrefix: 'Veuillez entrer le préfixe de lien personnalisé',
        uploadFolder: 'Veuillez entrer le chemin du dossier de téléchargement'
      }
    },
    theme: {
      light: 'Mode clair',
      dark: 'Mode sombre',
      auto: 'Mode automatique'
    },
    footer: {
      copyright: '© 2026 FileStation. Station de transfert de fichiers sécurisée et efficace'
    },
    userGuide: {
      title: 'Guide d\'utilisation',
      coreFeatures: {
        title: '1. Fonctionnalités principales',
        description: 'FileStation - Station de transfert de fichiers sécurisée et efficace',
        features: {
          upload: 'Téléchargement de fichiers : Supporte le glisser-déposer, le clic et le coller',
          security: 'Transfert sécurisé : La technologie de cryptage garantit la sécurité des fichiers',
          share: 'Partage rapide : Générez un code de retrait, partagez en un clic',
          expiration: 'Gestion de la validité : Supporte une durée de validité de 1 à 30 jours',
          multiLanguage: 'Support multilingue : Supporte le changement entre 7 langues'
        }
      },
      operationGuide: {
        title: '2. Guide d\'opération',
        upload: {
          title: 'Télécharger des fichiers',
          steps: [
            'Choisir la méthode de téléchargement : Faites glisser les fichiers vers la zone de téléchargement, cliquez sur la zone de téléchargement pour sélectionner les fichiers, ou copiez les fichiers et appuyez sur Ctrl + V pour coller',
            'Définir la durée de validité : Choisissez une durée de validité de 1 jour, 3 jours, 7 jours, 15 jours ou 30 jours',
            'Obtenir le code de retrait : Le code de retrait est généré automatiquement après le téléchargement, cliquez sur le bouton "Partager" pour générer le message de partage complet'
          ]
        },
        retrieve: {
          title: 'Récupérer des fichiers',
          steps: [
            'Entrez le code de retrait à 6 chiffres',
            'Cliquez sur le bouton "Récupérer"',
            'Téléchargez les fichiers'
          ]
        }
      },
      faq: {
        title: '3. Questions fréquentes',
        q1: {
          question: 'Q1 : Quels formats de fichiers sont pris en charge ?',
          answer: 'A : Tous les formats de fichiers courants sont pris en charge, y compris les images, vidéos, audio, documents, fichiers compressés, etc.'
        },
        q2: {
          question: 'Q2 : Y a-t-il une limite de taille de fichier ?',
          answer: 'A : La taille maximale d\'un seul fichier est de 2 Go.'
        },
        q3: {
          question: 'Q3 : Quelle est la durée de validité du code de retrait ?',
          answer: 'A : Défini lors du téléchargement, supporte 1 à 30 jours. Les fichiers sont automatiquement supprimés après expiration.'
        },
        q4: {
          question: 'Q4 : Que faire si le téléchargement du fichier échoue ?',
          answer: 'A : Vérifiez la connexion réseau, actualisez la page et téléchargez à nouveau.'
        },
        q5: {
          question: 'Q5 : Puis-je télécharger plusieurs fichiers à la fois ?',
          answer: 'A : Le téléchargement en lot est pris en charge, chaque fichier génère un code de retrait indépendant.'
        },
        q6: {
          question: 'Q6 : Comment partager des fichiers avec d\'autres ?',
          answer: 'A : Après le téléchargement, vous pouvez copier le code de retrait, le lien ou utiliser la fonction "Partager" pour générer un message complet.'
        }
      },
      notices: {
        title: '4. Remarques importantes',
        important: {
          title: 'Rappels importants',
          items: [
            'Validité du fichier : Téléchargez les fichiers à temps, ils ne peuvent pas être récupérés après expiration. Il est recommandé de partager avec les destinataires dès que possible après le téléchargement',
            'Sécurité du code de retrait : Conservez les codes de retrait en sécurité et ne les divulguez pas à des personnes non concernées',
            'Contenu du fichier : Ne téléchargez pas de contenu illégal ou interdit, ne téléchargez pas de fichiers impliquant la vie privée et la confidentialité',
            'Environnement réseau : Il est recommandé d\'utiliser dans un environnement réseau stable, ne fermez pas la page pendant le téléchargement de gros fichiers',
            'Compatibilité du navigateur : Il est recommandé d\'utiliser des navigateurs modernes tels que Chrome, Firefox, Edge, Safari'
          ]
        },
        suggestions: {
          title: 'Suggestions d\'utilisation',
          items: [
            'Nomination des fichiers : Utilisez des noms de fichiers clairs, évitez les caractères spéciaux et les espaces',
            'Paramètre de validité : Pour un partage temporaire, choisissez 1 à 3 jours, pour un partage à court terme, choisissez 7 jours, pour un partage à long terme, choisissez 15 à 30 jours',
            'Téléchargement en lot : Téléchargez plusieurs fichiers séparément, chaque fichier génère un code de retrait indépendant',
            'Méthode de partage : Le code de retrait convient à la messagerie instantanée, le lien convient aux e-mails et forums'
          ]
        }
      },
      toggle: {
        show: 'Afficher le guide d\'utilisation',
        hide: 'Masquer le guide d\'utilisation'
      }
    },
    announcement: {
      title: 'Annonce',
      confirm: 'J\'ai compris !'
    },
    history: {
      title: 'Historique des téléchargements'
    },
    language: {
      zhCN: 'Chinois',
      enUS: 'Anglais',
      jaJP: 'Japonais',
      koKR: 'Coréen',
      frFR: 'Français',
      deDE: 'Allemand',
      esES: 'Espagnol'
    }
  },
  'de-DE': {
    common: {
      title: 'FileStation',
      subtitle: 'Dateiübertragungsstation',
      description: 'Sichere und schnelle Dateiübertragungslösung',
      tagline: 'Legen Sie die Gültigkeitsdauer der Datei fest, erhalten Sie den Abholcode und teilen Sie Dateien einfach',
      mainTitle: 'Dateiübertragungszentrum',
      mainSubtitle: 'Sichere und schnelle Dateiübertragungsstation'
    },
    upload: {
      title: 'Dateien hochladen',
      dragHint: 'Ziehen',
      clickHint: 'Klicken',
      pasteHint: 'oder Strg + V zum Einfügen',
      expiration: 'Gültigkeitsdauer der Datei：',
      theme: 'Design：',
      expireOptions: {
        oneDay: '1 Tag',
        threeDays: '3 Tage',
        sevenDays: '7 Tage',
        fifteenDays: '15 Tage',
        thirtyDays: '30 Tage'
      },
      themeOptions: {
        auto: 'System folgen',
        light: 'Hell',
        dark: 'Dunkel'
      },
      pickupCode: 'Abholcode',
      expireTime: 'Ablauf',
      fileName: 'Dateiname',
      fileSize: 'Dateigröße',
      fileExpiry: 'Ablauf',
      shareTemplate: {
        sharedVia: 'Über FileStation freigegebene Datei',
        shareButton: 'Teilen',
        link: 'Link',
        extractionCode: 'Extraktionscode',
        validUntil: 'Gültig bis',
        copySuccess: 'Freigabeinhalt in die Zwischenablage kopiert!',
        copyFailed: 'Kopieren fehlgeschlagen, bitte manuell kopieren'
      }
    },
    retrieve: {
      title: 'Dateien abholen',
      inputPlaceholder: 'Bitte geben Sie den Abholcode ein',
      button: 'Abholen',
      error: {
        empty: 'Bitte geben Sie den Abholcode ein',
        invalid: 'Der Abholcode muss 6-stellig sein',
        notFound: 'Die Datei existiert nicht oder ist abgelaufen',
        failed: 'Download fehlgeschlagen. Bitte versuchen Sie es später erneut'
      }
    },
    settings: {
      urlForm: 'Linkformat-Einstellungen',
      upload: 'Upload-Einstellungen',
      defaultCopy: 'Standardlink kopieren',
      customUrl: 'Benutzerdefinierter Link',
      enableCustom: 'Benutzerdefiniert aktivieren',
      customPrefix: 'Benutzerdefiniertes Präfix',
      uploadChannel: 'Upload-Kanal',
      channelType: 'Kanaltyp',
      channelName: 'Kanalname',
      autoSelect: 'Automatische Auswahl',
      uploadFolder: 'Upload-Ordner',
      autoSwitch: 'Automatischer Wechsel',
      uploadNaming: 'Dateibenennungsmethode',
      filePreprocessing: 'Dateivorverarbeitung',
      convertToWebp: 'In WebP konvertieren',
      compress: 'Dateikomprimierung',
      compressThreshold: 'Komprimierungsschwelle',
      compressTarget: 'Zielgröße',
      serverCompress: 'Serverseitige Komprimierung',
      enableCompress: 'Komprimierung aktivieren',
      confirm: 'Bestätigen',
      urlTypes: {
        url: 'Original-Link',
        md: 'MarkDown',
        html: 'HTML',
        ubb: 'BBCode'
      },
      namingTypes: {
        default: 'Standard',
        index: 'Nur Präfix',
        origin: 'Nur ursprünglicher Name',
        short: 'Kurzlink'
      },
      channelTypes: {
        telegram: 'TG',
        cfr2: 'R2',
        s3: 'S3',
        discord: 'DC',
        huggingface: 'HF'
      },
      placeholders: {
        customUrlPrefix: 'Bitte geben Sie das benutzerdefinierte Link-Präfix ein',
        uploadFolder: 'Bitte geben Sie den Upload-Ordnerpfad ein'
      }
    },
    theme: {
      light: 'Heller Modus',
      dark: 'Dunkler Modus',
      auto: 'Automatischer Modus'
    },
    footer: {
      copyright: '© 2026 FileStation. Sichere und effiziente Dateiübertragungsstation'
    },
    userGuide: {
      title: 'Benutzerhandbuch',
      coreFeatures: {
        title: '1. Kernfunktionen',
        description: 'FileStation - Sichere und effiziente Dateiübertragungsstation',
        features: {
          upload: 'Datei-Upload: Unterstützt Drag & Drop, Klicken und Einfügen',
          security: 'Sichere Übertragung: Verschlüsselungstechnologie gewährleistet Dateisicherheit',
          share: 'Schnelles Teilen: Pickup-Code generieren, mit einem Klick teilen',
          expiration: 'Gültigkeitsverwaltung: Unterstützt Gültigkeitsdauer von 1-30 Tagen',
          multiLanguage: 'Mehrsprachige Unterstützung: Unterstützt Wechsel zwischen 7 Sprachen'
        }
      },
      operationGuide: {
        title: '2. Bedienungsanleitung',
        upload: {
          title: 'Dateien hochladen',
          steps: [
            'Upload-Methode wählen: Dateien in den Upload-Bereich ziehen, auf den Upload-Bereich klicken, um Dateien auszuwählen, oder Dateien kopieren und mit Strg + V einfügen',
            'Gültigkeitsdauer festlegen: Gültigkeitsdauer von 1 Tag, 3 Tagen, 7 Tagen, 15 Tagen oder 30 Tagen wählen',
            'Pickup-Code erhalten: Pickup-Code wird automatisch nach dem Upload generiert, auf "Teilen"-Button klicken, um vollständige Freigabenachricht zu generieren'
          ]
        },
        retrieve: {
          title: 'Dateien abrufen',
          steps: [
            '6-stelligen Pickup-Code eingeben',
            'Auf "Abrufen"-Button klicken',
            'Dateien herunterladen'
          ]
        }
      },
      faq: {
        title: '3. Häufig gestellte Fragen',
        q1: {
          question: 'F1: Welche Dateiformate werden unterstützt?',
          answer: 'A: Alle gängigen Dateiformate werden unterstützt, einschließlich Bilder, Videos, Audio, Dokumente, komprimierte Dateien usw.'
        },
        q2: {
          question: 'F2: Gibt es eine Dateigrößenbeschränkung?',
          answer: 'A: Die maximale Größe einer einzelnen Datei beträgt 2 GB.'
        },
        q3: {
          question: 'F3: Wie lange ist der Pickup-Code gültig?',
          answer: 'A: Wird beim Upload festgelegt, unterstützt 1-30 Tage. Dateien werden nach Ablauf automatisch gelöscht.'
        },
        q4: {
          question: 'F4: Was tun, wenn der Datei-Upload fehlschlägt?',
          answer: 'A: Überprüfen Sie die Netzwerkverbindung, aktualisieren Sie die Seite und laden Sie erneut hoch.'
        },
        q5: {
          question: 'F5: Kann ich mehrere Dateien gleichzeitig hochladen?',
          answer: 'A: Batch-Upload wird unterstützt, jede Datei generiert einen unabhängigen Pickup-Code.'
        },
        q6: {
          question: 'F6: Wie kann ich Dateien mit anderen teilen?',
          answer: 'A: Nach dem Upload können Sie den Pickup-Code, den Link kopieren oder die Funktion "Teilen" verwenden, um eine vollständige Nachricht zu generieren.'
        }
      },
      notices: {
        title: '4. Wichtige Hinweise',
        important: {
          title: 'Wichtige Erinnerungen',
          items: [
            'Dateigültigkeit: Laden Sie Dateien rechtzeitig herunter, sie können nach Ablauf nicht mehr abgerufen werden. Es wird empfohlen, Empfänger so schnell wie möglich nach dem Upload zu informieren',
            'Pickup-Code-Sicherheit: Bewahren Sie Pickup-Codes sicher auf und geben Sie sie nicht an unbeteiligte Personen weiter',
            'Dateiinhalt: Laden Sie keine illegale oder verbotene Inhalte hoch, laden Sie keine Dateien hoch, die Privatsphäre und Vertraulichkeit betreffen',
            'Netzwerkumgebung: Es wird empfohlen, in einer stabilen Netzwerkumgebung zu verwenden, schließen Sie die Seite während des Uploads großer Dateien nicht',
            'Browser-Kompatibilität: Es wird empfohlen, moderne Browser wie Chrome, Firefox, Edge, Safari zu verwenden'
          ]
        },
        suggestions: {
          title: 'Nutzungsvorschläge',
          items: [
            'Dateibenennung: Verwenden Sie klare Dateinamen, vermeiden Sie Sonderzeichen und Leerzeichen',
            'Gültigkeitseinstellung: Für temporäres Teilen 1-3 Tage, für kurzfristiges Teilen 7 Tage, für langfristiges Teilen 15-30 Tage wählen',
            'Batch-Upload: Laden Sie mehrere Dateien separat hoch, jede Datei generiert einen unabhängigen Pickup-Code',
            'Teilungsmethode: Pickup-Code eignet sich für Instant-Messaging, Link eignet sich für E-Mail und Foren'
          ]
        }
      },
      toggle: {
        show: 'Benutzerhandbuch anzeigen',
        hide: 'Benutzerhandbuch ausblenden'
      }
    },
    announcement: {
      title: 'Ankündigung',
      confirm: 'Verstanden!'
    },
    history: {
      title: 'Upload-Verlauf'
    },
    language: {
      zhCN: 'Chinesisch',
      enUS: 'Englisch',
      jaJP: 'Japanisch',
      koKR: 'Koreanisch',
      frFR: 'Französisch',
      deDE: 'Deutsch',
      esES: 'Spanisch'
    }
  },
  'es-ES': {
    common: {
      title: 'FileStation',
      subtitle: 'Estación de transferencia de archivos',
      description: 'Solución de transferencia de archivos segura y rápida',
      tagline: 'Establezca la duración de validez del archivo, obtenga el código de recuperación y comparta archivos fácilmente',
      mainTitle: 'Centro de transferencia de archivos',
      mainSubtitle: 'Estación de transferencia de archivos segura y rápida'
    },
    upload: {
      title: 'Subir archivos',
      dragHint: 'Arrastrar',
      clickHint: 'Hacer clic',
      pasteHint: 'o Ctrl + V para pegar',
      expiration: 'Período de validez del archivo：',
      theme: 'Tema：',
      expireOptions: {
        oneDay: '1 día',
        threeDays: '3 días',
        sevenDays: '7 días',
        fifteenDays: '15 días',
        thirtyDays: '30 días'
      },
      themeOptions: {
        auto: 'Seguir el sistema',
        light: 'Claro',
        dark: 'Oscuro'
      },
      pickupCode: 'Código de recogida',
      expireTime: 'Expiración',
      fileName: 'Nombre de archivo',
      fileSize: 'Tamaño de archivo',
      fileExpiry: 'Expiración',
      shareTemplate: {
        sharedVia: 'Archivo compartido a través de FileStation',
        shareButton: 'Compartir',
        link: 'Enlace',
        extractionCode: 'Código de extracción',
        validUntil: 'Válido hasta',
        copySuccess: '¡Contenido compartido copiado al portapapeles!',
        copyFailed: 'Error al copiar, por favor copia manualmente'
      }
    },
    retrieve: {
      title: 'Recuperar archivos',
      inputPlaceholder: 'Por favor ingrese el código de recuperación',
      button: 'Recuperar',
      error: {
        empty: 'Por favor ingrese el código de recuperación',
        invalid: 'El código de recuperación debe tener 6 dígitos',
        notFound: 'El archivo no existe o ha expirado',
        failed: 'Fallo en la descarga. Por favor intente más tarde'
      }
    },
    settings: {
      urlForm: 'Configuración del formato de enlace',
      upload: 'Configuración de subida',
      defaultCopy: 'Copiar enlace predeterminado',
      customUrl: 'Enlace personalizado',
      enableCustom: 'Activar personalización',
      customPrefix: 'Prefijo personalizado',
      uploadChannel: 'Canal de subida',
      channelType: 'Tipo de canal',
      channelName: 'Nombre del canal',
      autoSelect: 'Selección automática',
      uploadFolder: 'Carpeta de subida',
      autoSwitch: 'Cambio automático',
      uploadNaming: 'Método de nomenclatura de archivos',
      filePreprocessing: 'Preprocesamiento de archivos',
      convertToWebp: 'Convertir a WebP',
      compress: 'Compresión de archivos',
      compressThreshold: 'Umbral de compresión',
      compressTarget: 'Tamaño objetivo',
      serverCompress: 'Compresión del lado del servidor',
      enableCompress: 'Activar compresión',
      confirm: 'Confirmar',
      urlTypes: {
        url: 'Enlace original',
        md: 'MarkDown',
        html: 'HTML',
        ubb: 'BBCode'
      },
      namingTypes: {
        default: 'Predeterminado',
        index: 'Solo prefijo',
        origin: 'Solo nombre original',
        short: 'Enlace corto'
      },
      channelTypes: {
        telegram: 'TG',
        cfr2: 'R2',
        s3: 'S3',
        discord: 'DC',
        huggingface: 'HF'
      },
      placeholders: {
        customUrlPrefix: 'Por favor ingrese el prefijo de enlace personalizado',
        uploadFolder: 'Por favor ingrese la ruta de la carpeta de subida'
      }
    },
    theme: {
      light: 'Modo claro',
      dark: 'Modo oscuro',
      auto: 'Modo automático'
    },
    footer: {
      copyright: '© 2026 FileStation. Estación de transferencia de archivos segura y eficiente'
    },
    userGuide: {
      title: 'Guía de uso',
      coreFeatures: {
        title: '1. Características principales',
        description: 'FileStation - Estación de transferencia de archivos segura y eficiente',
        features: {
          upload: 'Subida de archivos: Soporta arrastrar, hacer clic y pegar',
          security: 'Transferencia segura: La tecnología de cifrado garantiza la seguridad de los archivos',
          share: 'Compartir rápido: Genera código de recogida, compartir con un clic',
          expiration: 'Gestión de validez: Soporta período de validez de 1-30 días',
          multiLanguage: 'Soporte multilingüe: Soporta cambio entre 7 idiomas'
        }
      },
      operationGuide: {
        title: '2. Guía de operación',
        upload: {
          title: 'Subir archivos',
          steps: [
            'Elegir método de subida: Arrastra los archivos al área de subida, haz clic en el área de subida para seleccionar archivos, o copia los archivos y presiona Ctrl + V para pegar',
            'Establecer período de validez: Elige período de validez de 1 día, 3 días, 7 días, 15 días o 30 días',
            'Obtener código de recogida: El código de recogida se genera automáticamente después de la subida, haz clic en el botón "Compartir" para generar mensaje de compartir completo'
          ]
        },
        retrieve: {
          title: 'Recuperar archivos',
          steps: [
            'Ingresa código de recogida de 6 dígitos',
            'Haz clic en el botón "Recuperar"',
            'Descarga archivos'
          ]
        }
      },
      faq: {
        title: '3. Preguntas frecuentes',
        q1: {
          question: 'P1: ¿Qué formatos de archivo son compatibles?',
          answer: 'A: Todos los formatos de archivo comunes son compatibles, incluyendo imágenes, videos, audio, documentos, archivos comprimidos, etc.'
        },
        q2: {
          question: 'P2: ¿Hay un límite de tamaño de archivo?',
          answer: 'A: El tamaño máximo de un solo archivo es 2GB.'
        },
        q3: {
          question: 'P3: ¿Cuál es el período de validez del código de recogida?',
          answer: 'A: Establecido durante la subida, soporta 1-30 días. Los archivos se eliminan automáticamente después de la expiración.'
        },
        q4: {
          question: 'P4: ¿Qué hacer si falla la subida del archivo?',
          answer: 'A: Verifica la conexión de red, actualiza la página y sube nuevamente.'
        },
        q5: {
          question: 'P5: ¿Puedo subir múltiples archivos a la vez?',
          answer: 'A: La subida por lotes es compatible, cada archivo genera un código de recogida independiente.'
        },
        q6: {
          question: 'P6: ¿Cómo compartir archivos con otros?',
          answer: 'A: Después de la subida, puedes copiar el código de recogida, el enlace o usar la función "Compartir" para generar un mensaje completo.'
        }
      },
      notices: {
        title: '4. Notas importantes',
        important: {
          title: 'Recordatorios importantes',
          items: [
            'Validez del archivo: Descarga los archivos a tiempo, no se pueden recuperar después de la expiración. Se recomienda compartir con los destinatarios lo antes posible después de la subida',
            'Seguridad del código de recogida: Mantén los códigos de recogida seguros y no los divulgues a personas no relacionadas',
            'Contenido del archivo: No subas contenido ilegal o prohibido, no subas archivos que involucren privacidad y confidencialidad',
            'Entorno de red: Se recomienda usar en un entorno de red estable, no cierres la página durante la subida de archivos grandes',
            'Compatibilidad del navegador: Se recomienda usar navegadores modernos como Chrome, Firefox, Edge, Safari'
          ]
        },
        suggestions: {
          title: 'Sugerencias de uso',
          items: [
            'Nomenclatura de archivos: Usa nombres de archivos claros, evita caracteres especiales y espacios',
            'Configuración de validez: Para compartir temporal elige 1-3 días, para compartir a corto plazo elige 7 días, para compartir a largo plazo elige 15-30 días',
            'Subida por lotes: Sube múltiples archivos por separado, cada archivo genera un código de recogida independiente',
            'Método de compartir: El código de recogida es adecuado para mensajería instantánea, el enlace es adecuado para correo electrónico y foros'
          ]
        }
      },
      toggle: {
        show: 'Mostrar guía de uso',
        hide: 'Ocultar guía de uso'
      }
    },
    announcement: {
      title: 'Anuncio',
      confirm: '¡Entendido!'
    },
    history: {
      title: 'Historial de subidas'
    },
    language: {
      zhCN: 'Chino',
      enUS: 'Inglés',
      jaJP: 'Japonés',
      koKR: 'Coreano',
      frFR: 'Francés',
      deDE: 'Alemán',
      esES: 'Español'
    }
  }
}

const i18n = createI18n({
  legacy: true,
  locale: detectUserLanguage(),
  fallbackLocale: 'zh-CN',
  messages
})

export default i18n
